import React, { Dispatch, FC, SetStateAction, useMemo, useState } from 'react';
import { Popover } from '@headlessui/react';
import moment from 'moment';
import ReactTooltip from 'react-tooltip';

import { ReactComponent as MoreIcon } from 'assets/icons/more.svg';
import { ReactComponent as SortArrow } from 'assets/icons/sort_arrow.svg';
import { ReactComponent as EmptyBucket } from 'assets/icons/empty-bucket.svg';
import { ReactComponent as RemovedInfo } from 'assets/icons/removed-info.svg';
import RebookingWarningMessage from 'components/RebookingWarningMessage/RebookingWarningMessage';

import ErrorIcon from 'components/ErrorIcon/ErrorIcon';
import {
  capitalizeAvatarName,
  concatFullName,
  getErrorReason,
  getIsWarningIconEnabled,
  isWarningIconExistedAfter24Hour,
} from 'utils/common';
import IndeterminateCheckbox from 'pages/DashboardPage/Table/IndeterminateCheckbox/IndeterminateCheckbox';
import {
  HeaderGroup,
  useFlexLayout,
  usePagination,
  useRowSelect,
  useSortBy,
  useTable,
} from 'react-table';
import LoadingSpinner from 'components/LoadingSpinner/LoadingSpinner';
import Pagination, { ITablePaginate } from 'components/Tables/Pagination';
import useAppointment, { IAppointmentStatus } from 'hooks/useAppointment';
import ThreeDotsOption from './ThreeDotsOption/ThreeDotsOption';

import ConfirmationModal from 'components/ConfirmationModal/ConfirmationModal';
import axiosInstance from 'apis/axiosInstance';
import { renderToast } from 'components/Toast/Toast';

import styles from './Table.module.css';
import AppointmentDetailModal from 'components/AppointmentDetailModal/AppointmentDetailModal';
import PractitionerNameColumn from 'components/TableColumn/PractitionerNameColumn/PractitionerNameColumn';
import AppointmentDateTimeColumn from 'components/TableColumn/AppointmentDateTimeColumn/AppointmentDateTimeColumn';
import useClinic from 'hooks/useClinic';
import PopoverOption from 'components/PopoverOption/PopoverOption';
import RebookingPopover from 'components/RebookingPopover/RebookingPopover';
import { APPOINTMENT_STATUS, ERROR_REASON } from 'utils/constants';
import { TRAFFIC_SOURCES } from 'pages/OnlineSchedulingReportPage/TrafficBySource/TRAFFIC_SOURCES';
import { formatFullPhoneNumber } from 'utils/formatPhoneNumber';
import ExcelExport from 'components/ExcelExport/ExcelExport';

const NON_SORTABLE_COLUMNS = ['patientPhoneNumber'];

const defaultTablePaginationState = {
  currentPage: 1,
  pageSize: 10,
};

interface TableProps {
  sort: 'asc' | 'desc';
  setSort: Dispatch<SetStateAction<'asc' | 'desc'>>;
  dateRange: { fromDate: string; toDate: string };
  sortBy: string;
  setSortBy: Dispatch<SetStateAction<string>>;
  status: IAppointmentStatus;
}

const Table: FC<TableProps> = ({
  sort,
  sortBy,
  status,
  dateRange,
  setSort,
  setSortBy,
}) => {
  const [pageSize, setPageSize] = useState(
    defaultTablePaginationState.pageSize
  );
  const [currentPage, setCurrentPage] = useState(
    defaultTablePaginationState.currentPage
  );

  const [highlightedRowId, setHighlighedRowId] = useState('');

  const [confirmModalMode, setConfirmModalMode] = useState('');

  const [selectedAppointmentId, setSelectedAppointmentId] = useState('');

  const { data: clinic } = useClinic();

  const { data, isLoading, mutate } = useAppointment({
    startDate: moment(dateRange.fromDate, 'YYYY-MM-DD')
      .startOf('days')
      .utc(true)
      .format(),

    endDate: moment(dateRange.toDate, 'YYYY-MM-DD')
      .endOf('days')
      .utc(true)
      .format(),
    limit: pageSize,
    page: currentPage,
    sort,
    sortBy,
    status,
    clinicIds: clinic ? [clinic.id] : [],
  });

  const isCheckBoxAndThreeDotsDisabled =
    status === 'completed' ||
    status === 'missed' ||
    status === 'cancelled' ||
    status === 'accepted';

  const columns = useMemo(
    () => [
      {
        Header: 'Appointment Date & Time',
        accessor: (field: any) => {
          return <AppointmentDateTimeColumn data={field} />;
        },
        id: 'appointmentDate',
      },
      {
        Header: 'Patient',
        accessor: (field: any) => {
          return (
            <div className="flex gap-x-0.8 items-center mr-[20px]">
              {field.patient ? (
                <>
                  <div className="w-2.4 h-2.4 bg-magenta rounded-full flex items-center justify-center text-white text-[1rem] shrink-0">
                    {field.patient
                      ? capitalizeAvatarName(
                          concatFullName({
                            firstName: field.patient.firstName,
                            lastName: field.patient.lastName,
                          })
                        )
                      : {}}
                  </div>
                  <div className="words-break">{field.patient.name}</div>
                </>
              ) : (
                <div />
              )}
            </div>
          );
        },
        id: 'patientName',
      },
      {
        Header: 'Phone Number',
        accessor: (field: any) => {
          if (!field.patient?.phoneNumber) {
            return null;
          }

          return (
            <div className="words-break pr-5">
              {formatFullPhoneNumber(`+${field.patient.phoneNumber}`)}
            </div>
          );
        },
        id: 'patientPhoneNumber',
      },
      {
        Header: 'Practitioner',
        accessor: (field: any) => {
          const isInactiveDoctor =
            field.linkedAppointment?.doctor?.inactive || field.doctor.inactive;

          return (
            <div className="pr-5">
              <PractitionerNameColumn field={field} />
              {isInactiveDoctor && (
                <>
                  <RemovedInfo
                    className="inline-block ml-0.6"
                    data-for={`inactive-doctor-${field.id}`}
                    data-tip=""
                  />
                  <ReactTooltip
                    id={`inactive-doctor-${field.id}`}
                    place="bottom"
                    effect="solid"
                    className={`${styles.tooltip} shadow-tooltip`}
                  >
                    <span className="text-11 text-darkest-grey flex h-full items-center normal-case">
                      The practitioner assigned to this appointment has been
                      inactivated in the PMS.
                    </span>
                  </ReactTooltip>
                </>
              )}
            </div>
          );
        },
        id: 'doctorName',
      },
      {
        Header: 'Service',
        accessor: (field: any) => {
          const isServiceAvailable =
            !field.service.deletedAt &&
            field.service.isActive &&
            field.service.isAvailable;

          return (
            <div className="words-break pr-5">
              {field.service.name}
              {!isServiceAvailable && (
                <>
                  <RemovedInfo
                    className="inline-block ml-0.6"
                    data-for={field.service.id}
                    data-tip=""
                  />
                  <ReactTooltip
                    id={field.service.id}
                    place="bottom"
                    effect="solid"
                    className={`${styles.tooltip} shadow-tooltip`}
                  >
                    <span className="text-11 text-darkest-grey flex h-full items-center normal-case">
                      This service has been deactivated or removed
                    </span>
                  </ReactTooltip>
                </>
              )}
            </div>
          );
        },
        width: 200,
        id: 'serviceName',
      },
      {
        Header: 'Source',
        accessor: (field: any) => {
          const source = TRAFFIC_SOURCES.find(
            ({ key }) => key === field.source
          );

          return (
            <div className="words-break pr-5">{source?.label || '—'}</div>
          );
        },
        id: 'source',
      },
      {
        Header: 'Requested Time',
        accessor: (field: any) => {
          const createdAtByTz = moment(field.createdAt).tz(field.timezone);

          return (
            <span>
              {createdAtByTz.format('MMM DD, YYYY')}
              <br />
              {createdAtByTz.format('hh:mm A Z UTC')}
            </span>
          );
        },
        id: 'createdAt',
      },
      {
        id: 'error_management',
        width: status === 'new' ? 72 : 40,
        Cell: ({ row }: any) => {
          const isAppointmentInThePast = moment(
            row.original.appointmentDate,
            'YYYY-MM-DD'
          ).isBefore(moment.tz(clinic?.timezone).format('YYYY-MM-DD'), 'day');

          const errorReason = getErrorReason(row.original);
          const awaitingList = (row.original as any)
            .awaitingAppointmentsByPatient;
          const isNewStatus = status === 'new';

          const isAppointmentAcceptedInThePast =
            isAppointmentInThePast &&
            row.original.status === APPOINTMENT_STATUS.ACCEPTED.KEY;

          const isIconOptionDisabled =
            status === APPOINTMENT_STATUS.ACCEPTED.KEY ||
            isAppointmentAcceptedInThePast;

          // EXPLICITLY DETECT IF ERROR ICON IS CLICKABLE
          const isErrorIconClickable = [
            APPOINTMENT_STATUS.NEW.KEY,
            APPOINTMENT_STATUS.ERROR.KEY,
            APPOINTMENT_STATUS.ALL.KEY,
            APPOINTMENT_STATUS.CANCELLED.KEY,
          ].includes(status);

          return (
            <div className="flex gap-x-1.6">
              <ErrorIcon appointment={{ ...row.original, errorReason }}>
                {status !== 'all' && isErrorIconClickable && (
                  <PopoverOption
                    appointment={row.original}
                    isAppointmentInThePast={isAppointmentInThePast}
                    isIconOptionDisabled={
                      status !== APPOINTMENT_STATUS.CANCELLED.KEY &&
                      isIconOptionDisabled
                    }
                  />
                )}
              </ErrorIcon>
              {isNewStatus && !!awaitingList?.length && (
                <ErrorIcon
                  appointment={{
                    ...row.original,
                    id: `${row.original.id}_rebooking`,
                    errorReason: ERROR_REASON.EXISTING_BOOKING_BY_PATIENT,
                    rebookingRecognitionDays:
                      awaitingList[0]?.rebookingRecognitionDays,
                  }}
                >
                  <RebookingPopover appointments={awaitingList} />
                </ErrorIcon>
              )}
              {!isNewStatus && !!awaitingList?.length && (
                <ErrorIcon
                  appointment={{
                    ...row.original,
                    id: `${row.original.id}_rebooking`,
                    errorReason: ERROR_REASON.EXISTING_BOOKING_BY_PATIENT,
                    rebookingRecognitionDays:
                      awaitingList[0]?.rebookingRecognitionDays,
                  }}
                />
              )}
            </div>
          );
        },
      },
      {
        id: 'action_btn',
        width: 25,
        Header: ({ selectedFlatRows }: any) => {
          const options = status === 'new' ? ['Accept'] : [];

          return (
            <Popover className="relative">
              <Popover.Button
                disabled={selectedFlatRows.length === 0}
                className="flex items-center disabled:cursor-not-allowed disabled:opacity-30"
              >
                <MoreIcon className="h-2.2" />
              </Popover.Button>

              <Popover.Panel className="flex flex-col gap-y-1.6 absolute py-2 right-0 z-10 w-16.5 bg-white shadow-primary rounded-[0.8rem] mt-0.6">
                {options.map((title) => (
                  <Popover.Button
                    key={title}
                    className="w-full px-2.5 hover:bg-opacity-10 hover:text-magenta text-left text-14 leaing-[2.1rem] text-darkest-grey"
                    onClick={() => setConfirmModalMode(title)}
                  >
                    {title}
                  </Popover.Button>
                ))}
              </Popover.Panel>
            </Popover>
          );
        },
        Cell: ({ row }: any) => {
          const appointment = row.original as any;

          if (status === 'all') {
            return null;
          }

          const isShownMoveToAccept = status === 'cancelled';

          const isAppointmentInThePast = moment(
            appointment.appointmentDate,
            'YYYY-MM-DD'
          ).isBefore(moment.tz(clinic?.timezone).format('YYYY-MM-DD'), 'day');

          const isWarningIconEnabled = getIsWarningIconEnabled(appointment);

          const isExistedAfter24Hour =
            isWarningIconExistedAfter24Hour(appointment);

          const isIconClickable = isWarningIconEnabled
            ? isExistedAfter24Hour
            : true;

          const isAcceptedApptInPast =
            isAppointmentInThePast &&
            appointment.status === APPOINTMENT_STATUS.ACCEPTED.KEY;

          if (
            !isShownMoveToAccept &&
            (isCheckBoxAndThreeDotsDisabled ||
              !isIconClickable ||
              isAcceptedApptInPast)
          ) {
            return null;
          }

          return (
            <ThreeDotsOption
              key={highlightedRowId}
              appointment={appointment}
              status={status}
              isAppointmentInThePast={isAppointmentInThePast}
            />
          );
        },
      },
    ],
    [clinic?.timezone, highlightedRowId, isCheckBoxAndThreeDotsDisabled, status]
  );

  const dataTable = useMemo(() => {
    if (isLoading) {
      return { data: [], metadata: null };
    }
    return { data: data.data, metadata: data.metadata };
  }, [data?.data, data?.metadata, isLoading]);

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    page,
    selectedFlatRows,
  } = useTable(
    {
      columns,
      data: dataTable.data ? dataTable.data : [],
      manualPagination: true,
      pageCount: dataTable.metadata ? data.metadata.total : 0,
    },
    useSortBy,
    usePagination,
    useRowSelect,
    useFlexLayout,
    (hooks) => {
      hooks.visibleColumns.push((columns) => [
        {
          id: 'selection',
          width: 50,
          Header: ({ rows, toggleRowSelected, selectedFlatRows }) => {
            const style = {
              cursor: 'pointer',
            };

            const numberOfNonErrorRows = rows.reduce((previous, current) => {
              const appointment = current.original as any;
              const isWarningIconEnabled = getIsWarningIconEnabled(appointment);
              const errorReason = getErrorReason(appointment);
              const isErrorIconDisplayed = isWarningIconEnabled || errorReason;

              if (!isErrorIconDisplayed) {
                return previous + 1;
              }
              return previous;
            }, 0);

            const checked: boolean =
              rows.length > 1 &&
              numberOfNonErrorRows > 0 &&
              selectedFlatRows.length === numberOfNonErrorRows;

            const indeterminate: boolean =
              !checked && selectedFlatRows.length > 0;

            const disabled: boolean = rows.length === 1;

            const overiddenOnChange = (
              event: React.ChangeEvent<HTMLInputElement>
            ) => {
              rows.forEach((row) => {
                const appointment = row.original as any;
                const isWarningIconEnabled =
                  getIsWarningIconEnabled(appointment);
                const errorReason = getErrorReason(appointment);
                const isErrorIconDisplayed =
                  isWarningIconEnabled || errorReason;

                if (!isErrorIconDisplayed) {
                  toggleRowSelected(row.id, event.currentTarget.checked);
                }
              });
            };

            const modifiedToggleAllRowsProps = {
              onChange: overiddenOnChange,
              style: style,
              checked: checked,
              indeterminate: indeterminate,
              disabled: disabled,
            };

            return (
              <div className="flex items-center pl-1.7">
                <IndeterminateCheckbox
                  {...modifiedToggleAllRowsProps}
                  isDisabled={
                    isCheckBoxAndThreeDotsDisabled ||
                    status === 'error' ||
                    status === 'all' ||
                    disabled
                  }
                />
              </div>
            );
          },
          Cell: ({ row, rows }) => {
            const appointment = row.original as any;
            const errorReason = !!getErrorReason(appointment);
            const isWarningIconEnabled = getIsWarningIconEnabled(appointment);
            const disabled: boolean = rows.length === 1;

            const isDisabled =
              errorReason ||
              isCheckBoxAndThreeDotsDisabled ||
              status === 'error' ||
              status === 'all' ||
              disabled ||
              isWarningIconEnabled;

            return (
              <div className="flex items-center pl-1.7">
                <IndeterminateCheckbox
                  isDisabled={isDisabled}
                  {...row.getToggleRowSelectedProps()}
                />
              </div>
            );
          },
        },
        ...columns,
      ]);
    }
  );

  const isActionColumn = (columnId: string) =>
    ['selection', 'action_btn', 'error_management'].includes(columnId);

  // Action columns are neither sortable nor clickable, while the columns listed
  // in NON_SORTABLE_COLUMNS still open the appointment detail modal on click.
  const isSortableColumn = (columnId: string) =>
    !isActionColumn(columnId) && !NON_SORTABLE_COLUMNS.includes(columnId);

  const handlePaginating = (data: ITablePaginate) => {
    setCurrentPage(data.perPage === pageSize ? data.currPage : 1);
    setPageSize(data.perPage);
  };

  const handleSortColumn = (column: HeaderGroup<object>) => {
    if (!isSortableColumn(column.id)) return;
    setCurrentPage(1);
    setSortBy(column.id);
    if (column.id === sortBy) {
      return setSort((sort) => (sort === 'asc' ? 'desc' : 'asc'));
    }
    setSort('asc');
  };

  const handleOnCellClicked = async (cell: any) => {
    if (isActionColumn(cell.column.id)) return;

    setSelectedAppointmentId(cell.row.original.id);
  };

  const content = `${selectedFlatRows.length} ${
    selectedFlatRows.length > 1 ? 'appointments' : 'appointment'
  }`;

  const handleSubmitConfirmation = async () => {
    try {
      const updatedStatus =
        confirmModalMode === 'Accept' ? 'accepted' : 'cancelled';

      const toastMessage =
        confirmModalMode === 'Accept' ? 'accepted' : 'rejected';

      const body = selectedFlatRows.map((row: any) => ({
        appointmentId: row.original.id,
        status: updatedStatus,
      }));

      await axiosInstance.patch('/appointments', body);
      await mutate();
      renderToast({
        type: 'success',
        message: `Successfully ${toastMessage} ${content}`,
      });
      setConfirmModalMode('');
    } catch (error) {
      renderToast({
        type: 'error',
        message: 'Something went wrong. Please try again later',
      });
    }
  };

  const renderTableBody = () => {
    if (isLoading) {
      return (
        <tbody>
          <tr>
            <td colSpan={6}>
              <div className="w-full h-[37rem] flex justify-center items-center">
                <LoadingSpinner className="all-child:fill-magenta" />
              </div>
            </td>
          </tr>
        </tbody>
      );
    }
    if (data.metadata.total === 0) {
      return (
        <tbody>
          <tr>
            <td colSpan={6}>
              <div className="w-full h-[24rem] flex flex-col gap-y-1.6 justify-center items-center">
                <EmptyBucket />
                <span>No appointment available on this list</span>
              </div>
            </td>
          </tr>
        </tbody>
      );
    }

    return (
      <tbody {...getTableBodyProps()} className="text-darkest-grey">
        {page.map((row) => {
          prepareRow(row);
          const awaitingList = (row.original as any)
            .awaitingAppointmentsByPatient;

          return (
            <tr
              onMouseEnter={() => setHighlighedRowId(row.id)}
              {...row.getRowProps()}
              className="bg-white border-b border-light-grey px-2 group hover:bg-lightPink items-center cursor-pointer relative"
              style={{
                ...(row.getRowProps().style || {}),
                minHeight: `calc(12rem + ${awaitingList.length * 50}px)`,
              }}
            >
              {row.cells.map((cell: any) => {
                return (
                  <td
                    onClick={() => handleOnCellClicked(cell)}
                    className={`text-left p-0 py-1.9 ${
                      awaitingList?.length ? 'self-start pt-1.9' : 'my-auto'
                    }`}
                    {...cell.getCellProps()}
                    style={{
                      ...(cell.getCellProps().style || {}),
                      minHeight: `calc(12rem + ${awaitingList.length * 50}px)`,
                    }}
                  >
                    {cell.render('Cell')}
                  </td>
                );
              })}
              {awaitingList?.length > 0 && (
                <div className="absolute bottom-0.5 left-[9%] right-[9%] max-w-[80%] z-[1] flex flex-col gap-y-1">
                  {awaitingList.map((child: any) => (
                    <RebookingWarningMessage
                      key={`msg-${child.id}`}
                      appointment={child}
                    />
                  ))}
                </div>
              )}
            </tr>
          );
        })}
      </tbody>
    );
  };

  return (
    <>
      {confirmModalMode && (
        <ConfirmationModal
          title={
            confirmModalMode === 'Accept'
              ? 'Accepting multiple appointments'
              : `Reject ${content}`
          }
          description={
            confirmModalMode === 'Accept'
              ? 'You are in the process of accepting multiple appointments. To proceed, click the "Yes, accept" button. Alternatively, you can review and take action on each appointment individually.'
              : `Are you sure you want to ${confirmModalMode.toLowerCase()} ${content}`
          }
          onClose={() => setConfirmModalMode('')}
          onSubmit={handleSubmitConfirmation}
          submitBtnTitle={`Yes, ${confirmModalMode.toLowerCase()}`}
        />
      )}
      {selectedAppointmentId && (
        <AppointmentDetailModal
          appointmentId={selectedAppointmentId}
          onClose={() => setSelectedAppointmentId('')}
          isHiddenAcceptRejectOptions={
            status === 'all' ||
            [
              APPOINTMENT_STATUS.ACCEPTED.KEY,
              APPOINTMENT_STATUS.CANCELLED.KEY,
              APPOINTMENT_STATUS.COMPLETED.KEY,
              APPOINTMENT_STATUS.MISSED.KEY,
            ].includes(status)
          } // For accepted, cancelled, completed, missed table, hide all options
        />
      )}
      <div className="flex flex-row gap-x-[1.6rem] absolute right-4 top-2.4">
        <ExcelExport
          appointmentStatus={status}
          clinicIds={clinic ? [clinic.id] : []}
          dateRange={dateRange}
          sort={sort}
          sortBy={sortBy}
          isDataLoading={isLoading}
        />
      </div>
      <table {...getTableProps()} className="w-full text-14 leading-[2.1rem]">
        <thead className="font-bold">
          {headerGroups.map((headerGroup) => (
            <tr
              {...headerGroup.getHeaderGroupProps()}
              className="px-2 items-center"
            >
              {headerGroup.headers.map((column) => {
                return (
                  <th
                    {...column.getHeaderProps()}
                    className="text-left p-0 py-1.9"
                  >
                    <div
                      className={`flex items-center gap-x-0.9${
                        isSortableColumn(column.id) ? ' cursor-pointer' : ''
                      }`}
                      onClick={() => handleSortColumn(column)}
                    >
                      <span className="uppercase text-dim-grey text-11 leading-[1.8rem]">
                        {column.render('Header')}
                      </span>

                      {isSortableColumn(column.id) && (
                        <span>
                          <SortArrow
                            className={`${
                              column.id === sortBy && 'fill-magenta'
                            } ${
                              column.id === sortBy &&
                              sort === 'asc' &&
                              'rotate-180'
                            } transition-all`}
                          />
                        </span>
                      )}
                    </div>
                  </th>
                );
              })}
            </tr>
          ))}
        </thead>
        {renderTableBody()}
      </table>
      {dataTable.metadata && dataTable.metadata.total > 0 && (
        <div className="py-1.6">
          <Pagination
            isBorderNotIncluded
            handlePaginating={handlePaginating}
            data={{
              currPage: dataTable.metadata.page,
              perPage: dataTable.metadata.limit,
              total: dataTable.metadata.total,
            }}
          />
        </div>
      )}
    </>
  );
};

export default Table;
